import { useActiveOrganization } from '@repo/api-client/react';
import type { Client } from '@repo/api-client/dtos';
import { useClients, useInvoices } from '@repo/queries';
import {
  ArrowDownRight,
  ArrowRight,
  ArrowUpRight,
  FileText,
  Mail,
  Plus,
  Send,
  Sparkles,
  Users,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useAiComposer } from '@/features/ai/AiComposerContext';
import { AiSuggestion } from '@/features/ai/AiSuggestion';
import { pickDashboardSuggestion } from '@/features/ai/dashboardSuggestion';
import { ClientAvatar, inferClientTag } from '@/features/avatar';
import { Page, PageHeader } from '@/features/common/PageHeader';
import { displayWireMoney } from '@/features/money';

type WireInvoice = NonNullable<ReturnType<typeof useInvoices>['invoices']>[number];

const DAY = 24 * 60 * 60 * 1000;
const PERIOD_DAYS = 30;
const TREND_WEEKS = 8;
const TOP_CLIENTS = 5;

/**
 * Authenticated landing screen. The hero is no longer "one big number
 * + a sea of whitespace": we lay out four KPI tiles, a cashflow trend,
 * a status donut, top clients, AI suggestions, and the recent
 * invoices grid. Everything still derives from the live `useInvoices`
 * stream so we don't introduce a second source of truth.
 */
export function DashboardScreen() {
  const org = useActiveOrganization();
  const { invoices, loading } = useInvoices();
  const { clients } = useClients();
  const navigate = useNavigate();
  const { open: openAi } = useAiComposer();
  const [suggestionDismissed, setSuggestionDismissed] = useState(false);

  const currency = org?.currency ?? 'USD';
  const suggestion = useMemo(
    () => pickDashboardSuggestion({ invoices: invoices ?? [] }),
    [invoices],
  );
  const summary = useMemo(() => buildSummary(invoices ?? [], Date.now()), [invoices]);
  const recent = (invoices ?? []).slice(0, 6);
  // Map client id → row so we can paint the top-clients list with
  // their photo. Cheap (already cached by `useClients` upstream)
  // and lets the dashboard match the client list / invoice list
  // visual language without an extra round-trip.
  const clientById = useMemo(() => {
    const m = new Map<string, Client>();
    for (const c of clients ?? []) m.set(c.id, c);
    return m;
  }, [clients]);

  return (
    <Page>
      <PageHeader
        title={`Welcome back${org ? `, ${org.nickname || org.name}` : ''}`}
        description="At-a-glance for your active business — outstanding amount, cashflow trend, and the work most likely to move the needle today."
        actions={
          <>
            <Button variant="outline" asChild>
              <Link to="/invoices">
                <FileText className="size-4" /> All invoices
              </Link>
            </Button>
            <Button asChild>
              <Link to="/invoices/new">
                <Plus className="size-4" /> New invoice
              </Link>
            </Button>
          </>
        }
      />

      <section className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <KpiTile
          label="Outstanding"
          loading={loading}
          value={displayWireMoney(summary.outstandingMinor.toString(), currency)}
          delta={summary.outstandingDelta}
          tone="ink"
          subtitle={pluralise(summary.openCount, 'open invoice', 'open invoices')}
        />
        <KpiTile
          label={`Paid · last ${PERIOD_DAYS}d`}
          loading={loading}
          value={displayWireMoney(summary.paidMinor.toString(), currency)}
          delta={summary.paidDelta}
          tone="success"
          subtitle={pluralise(summary.paidCount, 'invoice', 'invoices')}
        />
        <KpiTile
          label="Overdue"
          loading={loading}
          value={
            summary.overdueCount > 0
              ? displayWireMoney(summary.overdueMinor.toString(), currency)
              : '—'
          }
          tone={summary.overdueCount > 0 ? 'danger' : 'muted'}
          subtitle={
            summary.overdueCount > 0
              ? pluralise(summary.overdueCount, 'invoice past due', 'invoices past due')
              : 'Nothing past due — nice.'
          }
        />
        <KpiTile
          label="Drafts"
          loading={loading}
          value={String(summary.draftCount)}
          tone="muted"
          subtitle={
            summary.draftCount > 0
              ? 'In your drawer waiting to be sent.'
              : 'No drafts open.'
          }
        />
      </section>

      {!loading && !suggestionDismissed ? (
        <section className="mt-4">
          <AiSuggestion
            {...suggestion}
            onDismiss={() => setSuggestionDismissed(true)}
            onAccept={() => openAi({ prompt: suggestion.prompt })}
          />
        </section>
      ) : null}

      <section className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader className="flex-row items-center justify-between">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                Cashflow
              </p>
              <CardTitle className="text-lg">Paid by week · last {TREND_WEEKS}w</CardTitle>
            </div>
            <Badge variant="outline" className="font-mono">
              {displayWireMoney(summary.trendTotalMinor.toString(), currency)}
            </Badge>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-[180px] w-full rounded-lg" />
            ) : (
              <CashflowChart
                buckets={summary.weeklyBuckets}
                currency={currency}
                emptyHint={summary.trendTotalMinor === 0n ? 'No payments yet — send your first invoice to see the line move.' : null}
              />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Pipeline
            </p>
            <CardTitle className="text-lg">Status breakdown</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col items-center gap-4">
            {loading ? (
              <Skeleton className="h-36 w-36 rounded-full" />
            ) : (
              <StatusDonut counts={summary.counts} />
            )}
            <ul className="w-full space-y-1.5 text-sm">
              {STATUS_ROWS.map(({ key, label, dot }) => (
                <li
                  key={key}
                  className="flex items-center justify-between gap-3 text-muted-foreground"
                >
                  <span className="flex items-center gap-2">
                    <span
                      aria-hidden
                      className="size-2 rounded-full"
                      style={{ background: dot }}
                    />
                    {label}
                  </span>
                  <span className="font-mono text-foreground">
                    {summary.counts[key]}
                  </span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </section>

      <section className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader className="flex-row items-center justify-between">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                Top clients
              </p>
              <CardTitle className="text-lg">By outstanding balance</CardTitle>
            </div>
            <Button variant="link" asChild>
              <Link to="/clients">
                All clients <ArrowRight className="size-3.5" />
              </Link>
            </Button>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {Array.from({ length: 4 }).map((_, idx) => (
                  <Skeleton key={idx} className="h-9 w-full rounded-md" />
                ))}
              </div>
            ) : summary.topClients.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                No outstanding balances right now. Time to draft something new.
              </p>
            ) : (
              <ul className="divide-y divide-line">
                {summary.topClients.map((client) => {
                  const pct =
                    summary.outstandingMinor === 0n
                      ? 0
                      : Number((client.amountMinor * 100n) / summary.outstandingMinor);
                  const c = clientById.get(client.clientId);
                  return (
                    <li key={client.clientId}>
                      <Link
                        to={`/clients/${client.clientId}`}
                        className="flex items-center gap-3 py-2.5 transition-colors hover:bg-secondary/40"
                      >
                        <ClientAvatar
                          name={client.name}
                          photoUrl={c?.photoUrl ?? null}
                          tag={inferClientTag({
                            name: client.name,
                            company: c?.company,
                          })}
                          size={32}
                          fontSize={12}
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-baseline justify-between gap-3">
                            <span className="truncate text-sm font-medium text-foreground">
                              {client.name}
                            </span>
                            <span className="font-mono text-sm tabular-nums text-foreground">
                              {displayWireMoney(client.amountMinor.toString(), currency)}
                            </span>
                          </div>
                          <div className="mt-1.5 flex items-center gap-2 text-xs text-muted-foreground">
                            <div className="h-1 flex-1 overflow-hidden rounded-full bg-secondary">
                              <div
                                className="h-full bg-accent"
                                style={{ width: `${Math.max(4, pct)}%` }}
                              />
                            </div>
                            <span className="tabular-nums">
                              {pluralise(client.invoiceCount, 'invoice', 'invoices')}
                            </span>
                          </div>
                        </div>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-b from-accent-soft/60 to-transparent">
          <CardHeader>
            <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-accent">
              <Sparkles className="size-3.5" /> AI suggestions
            </p>
            <CardTitle className="text-lg">Move something forward</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-2.5">
            {summary.suggestions.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                You're caught up. Ask the assistant for ideas — or draft a new
                invoice.
              </p>
            ) : (
              summary.suggestions.map((s, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => {
                    if (s.action.kind === 'ai-prompt') {
                      openAi({ prompt: s.action.prompt });
                    } else {
                      navigate(s.action.to);
                    }
                  }}
                  className="group flex items-start gap-3 rounded-lg border border-line bg-card/70 p-3 text-left transition-colors hover:border-accent/40 hover:bg-card"
                >
                  <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-md bg-accent-soft text-accent">
                    <s.Icon className="size-3.5" />
                  </span>
                  <span className="flex-1">
                    <span className="block text-sm font-medium text-foreground">
                      {s.title}
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      {s.body}
                    </span>
                  </span>
                  <ArrowRight className="mt-1 size-3.5 shrink-0 text-muted-foreground transition-colors group-hover:text-accent" />
                </button>
              ))
            )}
          </CardContent>
        </Card>
      </section>

      <section className="mt-6">
        <div className="mb-3 flex items-end justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-[0.16em] text-muted-foreground">
            Recent invoices
          </h2>
          <Button variant="link" asChild>
            <Link to="/invoices">
              All invoices <ArrowRight className="size-3.5" />
            </Link>
          </Button>
        </div>
        {loading ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, idx) => (
              <Skeleton key={idx} className="h-28 rounded-xl" />
            ))}
          </div>
        ) : recent.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center gap-3 py-10 text-center">
              <p className="text-sm text-muted-foreground">
                No invoices yet — draft your first one to start getting paid.
              </p>
              <Button asChild>
                <Link to="/invoices/new">
                  <Plus className="size-4" /> New invoice
                </Link>
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {recent.map((inv) => {
              const c = clientById.get(inv.clientId);
              return (
                <Link
                  key={inv.id}
                  to={`/invoices/${inv.id}`}
                  className="block rounded-xl border border-line bg-card p-5 transition-colors hover:border-line-strong"
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-sm font-semibold text-foreground">
                      {inv.invoiceNumber}
                    </span>
                    <Badge variant={statusVariant(inv.status)}>{inv.status}</Badge>
                  </div>
                  <div className="mt-2 flex items-center gap-2.5">
                    <ClientAvatar
                      name={inv.clientNameSnapshot || '?'}
                      photoUrl={c?.photoUrl ?? null}
                      tag={inferClientTag({
                        name: inv.clientNameSnapshot ?? '',
                        company: c?.company,
                      })}
                      size={28}
                      fontSize={11}
                    />
                    <p className="truncate text-xs text-muted-foreground">
                      {inv.clientNameSnapshot}
                    </p>
                  </div>
                  <p className="mt-3 font-mono text-xl font-semibold text-foreground">
                    {displayWireMoney(inv.totals.amountDue, inv.currency)}
                  </p>
                </Link>
              );
            })}
          </div>
        )}
      </section>
    </Page>
  );
}

// ---------------------------------------------------------------------------
// Tiles + chart primitives
// ---------------------------------------------------------------------------

type Tone = 'ink' | 'success' | 'danger' | 'muted';

function KpiTile({
  label,
  value,
  subtitle,
  delta,
  tone = 'ink',
  loading,
}: {
  label: string;
  value: string;
  subtitle?: string;
  delta?: number | null;
  tone?: Tone;
  loading?: boolean;
}) {
  const valueClass =
    tone === 'success'
      ? 'text-success'
      : tone === 'danger'
        ? 'text-destructive'
        : tone === 'muted'
          ? 'text-muted-foreground'
          : 'text-foreground';
  return (
    <Card className="overflow-hidden">
      <CardContent className="flex flex-col gap-2 p-5">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          {label}
        </p>
        {loading ? (
          <Skeleton className="h-9 w-32" />
        ) : (
          <p className={`font-display text-3xl font-semibold tracking-tight ${valueClass}`}>
            {value}
          </p>
        )}
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {subtitle ? <span>{subtitle}</span> : null}
          {delta !== undefined && delta !== null && Number.isFinite(delta) ? (
            <DeltaPill delta={delta} />
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}

function DeltaPill({ delta }: { delta: number }) {
  if (delta === 0) return null;
  const positive = delta > 0;
  const Icon = positive ? ArrowUpRight : ArrowDownRight;
  const tone = positive
    ? 'text-success bg-success-soft'
    : 'text-destructive bg-[var(--danger-soft)]';
  return (
    <span
      className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-medium tabular-nums ${tone}`}
    >
      <Icon className="size-3" />
      {Math.abs(Math.round(delta))}%
    </span>
  );
}

interface WeeklyBucket {
  weekStart: number;
  amountMinor: bigint;
  label: string;
}

function CashflowChart({
  buckets,
  currency,
  emptyHint,
}: {
  buckets: WeeklyBucket[];
  currency: string;
  emptyHint: string | null;
}) {
  const max = buckets.reduce(
    (acc, b) => (b.amountMinor > acc ? b.amountMinor : acc),
    0n,
  );
  if (max === 0n && emptyHint) {
    return (
      <div className="flex h-[180px] items-center justify-center rounded-lg border border-dashed border-line bg-secondary/30 text-center text-sm text-muted-foreground">
        <span className="max-w-xs">{emptyHint}</span>
      </div>
    );
  }
  return (
    <div className="flex h-[180px] items-end gap-2">
      {buckets.map((b, idx) => {
        const heightPct =
          max === 0n ? 0 : Math.max(4, Number((b.amountMinor * 100n) / max));
        return (
          <div key={idx} className="group flex flex-1 flex-col items-center gap-1.5">
            <div className="relative flex w-full flex-1 items-end">
              <div
                className="w-full rounded-t-md bg-accent/80 transition-all group-hover:bg-accent"
                style={{ height: `${heightPct}%` }}
                title={`${b.label}: ${displayWireMoney(b.amountMinor.toString(), currency)}`}
              />
            </div>
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
              {b.label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

const STATUS_ROWS = [
  { key: 'sent', label: 'Sent', dot: 'var(--accent)' },
  { key: 'pending', label: 'Pending', dot: 'var(--warning)' },
  { key: 'overdue', label: 'Overdue', dot: 'var(--danger)' },
  { key: 'paid', label: 'Paid', dot: 'var(--success)' },
  { key: 'draft', label: 'Drafts', dot: 'var(--muted)' },
] as const;

function StatusDonut({ counts }: { counts: Counts }) {
  const total = STATUS_ROWS.reduce((acc, row) => acc + counts[row.key], 0);
  if (total === 0) {
    return (
      <div className="flex size-36 items-center justify-center rounded-full border-2 border-dashed border-line text-xs text-muted-foreground">
        No invoices
      </div>
    );
  }
  const r = 56;
  const c = 2 * Math.PI * r;
  let offset = 0;
  const segments = STATUS_ROWS.flatMap((row) => {
    const value = counts[row.key];
    if (value === 0) return [];
    const length = (value / total) * c;
    const segment = (
      <circle
        key={row.key}
        cx={72}
        cy={72}
        r={r}
        fill="none"
        stroke={row.dot}
        strokeWidth={16}
        strokeDasharray={`${length} ${c - length}`}
        strokeDashoffset={-offset}
        // Round caps look bad when adjacent slices are very thin and butt
        // up against each other; keep them flat.
        strokeLinecap="butt"
      />
    );
    offset += length;
    return [segment];
  });
  return (
    <div className="relative size-36">
      <svg
        viewBox="0 0 144 144"
        className="size-full -rotate-90"
        role="img"
        aria-label="Status breakdown"
      >
        <circle cx={72} cy={72} r={r} fill="none" stroke="var(--line)" strokeWidth={16} />
        {segments}
      </svg>
      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-display text-2xl font-semibold leading-none text-foreground">
          {total}
        </span>
        <span className="mt-1 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          invoices
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Aggregation
// ---------------------------------------------------------------------------

type StatusKey = 'draft' | 'pending' | 'sent' | 'paid' | 'overdue' | 'cancelled';
type Counts = Record<StatusKey, number>;

interface DashboardSummary {
  outstandingMinor: bigint;
  paidMinor: bigint;
  overdueMinor: bigint;
  trendTotalMinor: bigint;
  outstandingDelta: number | null;
  paidDelta: number | null;
  openCount: number;
  paidCount: number;
  overdueCount: number;
  draftCount: number;
  counts: Counts;
  weeklyBuckets: WeeklyBucket[];
  topClients: Array<{
    clientId: string;
    name: string;
    amountMinor: bigint;
    invoiceCount: number;
  }>;
  suggestions: Suggestion[];
}

function isStatusKey(value: string): value is StatusKey {
  return (
    value === 'draft' ||
    value === 'pending' ||
    value === 'sent' ||
    value === 'paid' ||
    value === 'overdue' ||
    value === 'cancelled'
  );
}

interface Suggestion {
  title: string;
  body: string;
  Icon: typeof Sparkles;
  action:
    | { kind: 'ai-prompt'; prompt: string }
    | { kind: 'navigate'; to: string };
}

function buildSummary(invoices: WireInvoice[], now: number): DashboardSummary {
  const counts: Counts = {
    draft: 0,
    pending: 0,
    sent: 0,
    paid: 0,
    overdue: 0,
    cancelled: 0,
  };
  let outstandingMinor = 0n;
  let outstandingPrevMinor = 0n;
  let paidMinor = 0n;
  let paidPrevMinor = 0n;
  let overdueMinor = 0n;
  let paidCount = 0;
  let overdueCount = 0;
  let openCount = 0;

  const periodStart = now - PERIOD_DAYS * DAY;
  const previousPeriodStart = now - 2 * PERIOD_DAYS * DAY;
  const trendStart = startOfWeekUTC(now - (TREND_WEEKS - 1) * 7 * DAY);

  // Per-client aggregation (only invoices that contribute to outstanding).
  const clientTotals = new Map<
    string,
    { name: string; amountMinor: bigint; invoiceCount: number }
  >();

  // Weekly buckets, indexed by week-start timestamp.
  const buckets = new Map<number, bigint>();
  for (let i = 0; i < TREND_WEEKS; i += 1) {
    buckets.set(trendStart + i * 7 * DAY, 0n);
  }

  for (const inv of invoices) {
    if (isStatusKey(inv.status)) counts[inv.status] += 1;
    const amountDue = BigInt(inv.totals.amountDue);
    const amountPaid = BigInt(inv.totals.amountPaid);
    const issuedAt = parseDate(inv.issueDate);

    if (
      inv.status === 'sent' ||
      inv.status === 'pending' ||
      inv.status === 'overdue'
    ) {
      outstandingMinor += amountDue;
      openCount += 1;

      const bucket = clientTotals.get(inv.clientId) ?? {
        name: inv.clientNameSnapshot,
        amountMinor: 0n,
        invoiceCount: 0,
      };
      bucket.amountMinor += amountDue;
      bucket.invoiceCount += 1;
      clientTotals.set(inv.clientId, bucket);

      if (issuedAt !== null && issuedAt >= previousPeriodStart && issuedAt < periodStart) {
        outstandingPrevMinor += amountDue;
      }
    }

    if (inv.status === 'overdue') {
      overdueMinor += amountDue;
      overdueCount += 1;
    }

    if (inv.status === 'paid') {
      paidCount += 1;
      if (issuedAt !== null && issuedAt >= periodStart) {
        paidMinor += amountPaid;
      } else if (
        issuedAt !== null &&
        issuedAt >= previousPeriodStart &&
        issuedAt < periodStart
      ) {
        paidPrevMinor += amountPaid;
      }

      // Cashflow trend: drop the payment into the week of the issue date
      // (paidAt isn't in the wire DTO; issueDate is the closest signal we
      // have without round-tripping payments separately).
      if (issuedAt !== null) {
        const weekStart = startOfWeekUTC(issuedAt);
        if (buckets.has(weekStart)) {
          buckets.set(weekStart, (buckets.get(weekStart) ?? 0n) + amountPaid);
        }
      }
    }
  }

  const weeklyBuckets: WeeklyBucket[] = Array.from(buckets.entries())
    .sort(([a], [b]) => a - b)
    .map(([weekStart, amountMinor]) => ({
      weekStart,
      amountMinor,
      label: shortWeekLabel(weekStart),
    }));

  const trendTotalMinor = weeklyBuckets.reduce(
    (acc, b) => acc + b.amountMinor,
    0n,
  );

  const topClients = Array.from(clientTotals.entries())
    .map(([clientId, value]) => ({ clientId, ...value }))
    .sort((a, b) =>
      a.amountMinor === b.amountMinor ? 0 : a.amountMinor > b.amountMinor ? -1 : 1,
    )
    .slice(0, TOP_CLIENTS);

  const suggestions = buildSuggestions(invoices, {
    overdueCount,
    overdueMinor,
    draftCount: counts.draft,
    topClient: topClients[0] ?? null,
  });

  return {
    outstandingMinor,
    paidMinor,
    overdueMinor,
    trendTotalMinor,
    outstandingDelta: deltaPct(outstandingMinor, outstandingPrevMinor),
    paidDelta: deltaPct(paidMinor, paidPrevMinor),
    openCount,
    paidCount,
    overdueCount,
    draftCount: counts.draft,
    counts,
    weeklyBuckets,
    topClients,
    suggestions,
  };
}

function buildSuggestions(
  invoices: WireInvoice[],
  ctx: {
    overdueCount: number;
    overdueMinor: bigint;
    draftCount: number;
    topClient: { name: string; amountMinor: bigint } | null;
  },
): Suggestion[] {
  const out: Suggestion[] = [];
  if (ctx.overdueCount > 0) {
    out.push({
      title: `Send ${ctx.overdueCount} overdue ${ctx.overdueCount === 1 ? 'reminder' : 'reminders'}`,
      body: 'Polite, on-brand follow-up — drafted by the assistant.',
      Icon: Mail,
      action: {
        kind: 'ai-prompt',
        prompt: `Draft polite payment reminders for my ${ctx.overdueCount} overdue invoice${ctx.overdueCount === 1 ? '' : 's'}.`,
      },
    });
  }
  if (ctx.draftCount > 0) {
    out.push({
      title: `Send ${ctx.draftCount} pending ${ctx.draftCount === 1 ? 'draft' : 'drafts'}`,
      body: 'Drafts in your drawer waiting on a final review.',
      Icon: Send,
      action: { kind: 'navigate', to: '/invoices?status=draft' },
    });
  }
  if (ctx.topClient && ctx.topClient.amountMinor > 0n) {
    out.push({
      title: `Follow up with ${ctx.topClient.name}`,
      body: 'Largest outstanding balance — might be worth a check-in.',
      Icon: Sparkles,
      action: {
        kind: 'ai-prompt',
        prompt: `Draft a friendly check-in email to ${ctx.topClient.name} about their open balance.`,
      },
    });
  }
  if (invoices.length === 0) {
    out.push({
      title: 'Draft your first invoice',
      body: 'Tell the assistant what you delivered — it will fill the form.',
      Icon: Plus,
      action: {
        kind: 'ai-prompt',
        prompt: 'Help me draft my first invoice.',
      },
    });
  }
  return out.slice(0, 3);
}

function deltaPct(curr: bigint, prev: bigint): number | null {
  if (prev === 0n) return null;
  const c = Number(curr);
  const p = Number(prev);
  if (!Number.isFinite(c) || !Number.isFinite(p) || p === 0) return null;
  return ((c - p) / p) * 100;
}

function parseDate(value: string | null | undefined): number | null {
  if (!value) return null;
  const t = Date.parse(value);
  return Number.isFinite(t) ? t : null;
}

function startOfWeekUTC(ms: number): number {
  // Anchor weeks on Monday UTC. Stable, locale-independent — we just
  // need every payment to land in a deterministic bucket.
  const d = new Date(ms);
  const day = d.getUTCDay(); // 0=Sun..6=Sat
  const offset = (day + 6) % 7; // days since Monday
  const monday = Date.UTC(
    d.getUTCFullYear(),
    d.getUTCMonth(),
    d.getUTCDate() - offset,
  );
  return monday;
}

function shortWeekLabel(ms: number): string {
  const d = new Date(ms);
  // e.g. "5/4" — concise enough to fit in narrow bars.
  return `${d.getUTCMonth() + 1}/${d.getUTCDate()}`;
}

function pluralise(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

function statusVariant(
  status: string,
): 'success' | 'warning' | 'danger' | 'default' | 'accent' {
  switch (status) {
    case 'paid':
      return 'success';
    case 'overdue':
      return 'danger';
    case 'sent':
    case 'pending':
      return 'warning';
    case 'draft':
      return 'default';
    default:
      return 'default';
  }
}

// Re-export for the regression test in __tests__/Dashboard.totals.test.ts
// to share the same code path the screen uses.
export { buildSummary as __buildSummaryForTest };
